import { useMemo, useState, type Ref } from "react";
import {
  Box,
  Icon,
  Input,
  MenuCheckboxItem,
  MenuGroup,
  MenuGroupLabel,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  Text,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import type { IssueScope } from "#app/lib/filters/issue-scope";
import { scopeWorkflowId } from "#app/lib/filters/issue-scope";
import { hasQuickFilterValue } from "#app/lib/filters/quick-values";
import { scopeQuickFilterDefs } from "#app/lib/filters/scoped-quick-filters";
import type {
  FilterConfig,
  QuickFilterLayerFor,
  UseFiltersResult,
} from "#app/lib/filters/types";
import { useQuickSearchEntities } from "#app/lib/hooks/use-quick-filter-directory";
import {
  searchKinds,
  searchOptions,
  useQuickFilterOptions,
  type QuickFilterOption,
  type QuickFilterSection,
  QUICK_SEARCH_MATCH_CAP,
  writeQuickValue,
} from "./quick-inputs/quick-filter-options";
import { QuickDateSubmenu } from "./quick-inputs/quick-date-submenu";
import { QuickFilterValue } from "./quick-inputs/quick-filter-value";
import { QuickOptionFace } from "./quick-inputs/quick-option-face";
import { QuickOptionSubmenu } from "./quick-inputs/quick-option-submenu";
import {
  isEntityPickerSection,
  QuickEntityPicker,
} from "#app/components/filters/quick-inputs/quick-entity-picker";
import { useAssigneeRoster } from "./use-assignee-roster";
import { useFilterTypeAvailability } from "./use-filter-type-availability";


type QuickFilterMenuProps = Omittable<{
  config: FilterConfig;
  filters: UseFiltersResult;
  /** The screen's scope, when it has one. A value the scope already pins to
   *  exactly one is not worth asking about — see `scopeQuickFilterDefs`. */
  scope?: IssueScope;
  /** The layer each field lives in — see `QuickFilterLayerFor`. Defaults to
   *  `filters` for every field. */
  layerFor?: QuickFilterLayerFor;
  /** The search field, so the host menu can send typing to it — see
   *  `FilterPanel`'s type-to-search. */
  searchRef?: Ref<HTMLInputElement>;
}>;

type MenuGroupRun = {
  key: string;
  /** Absent for an ungrouped run — the archived flag stands alone. Names the
   *  group for assistive tech only; the run is drawn as hairlines. */
  title: string | undefined;
  sections: QuickFilterSection[];
};

/** Filters collected under their `group`, in first-appearance order. */
function groupSections(sections: QuickFilterSection[]): MenuGroupRun[] {
  const runs: MenuGroupRun[] = [];
  const byKey = new Map<string, MenuGroupRun>();
  for (const section of sections) {
    const key = section.def.group ?? "";
    let run = byKey.get(key);
    if (!run) {
      run = { key, title: section.def.group, sections: [] };
      byKey.set(key, run);
      runs.push(run);
    }
    run.sections.push(section);
  }
  return runs;
}

/**
 * Every quick filter, as ONE menu whose rows open their values on HOVER.
 *
 * A submenu, not a disclosure and not a sub-page: the row list never moves, so
 * sliding from Assignee to Stage swaps the panel beside it with no press at
 * all, and there is nothing to go Back from. Base UI's submenu owns the part
 * that makes hover survivable — the grace area that lets the pointer travel
 * diagonally into an open panel without the row it passes over stealing it.
 * A popover opened on `mouseenter` has none of that and thrashes.
 *
 * Every row reads `icon label … value ▸` and states its value AS the value
 * (`QuickFilterValue`), so the applied set is legible without opening anything
 * — which is why this panel carries no chip band and no count of its own.
 *
 * `MenuCheckboxItem` is what the values are, so a press toggles WITHOUT closing
 * the menu; the check is the applied state, and unchecking is how one value
 * comes off. There is exactly one clear, the panel header's "Clear all".
 */
export function QuickFilterMenu({
  config,
  filters,
  scope,
  layerFor: layerForProp,
  searchRef,
}: QuickFilterMenuProps) {
  const [query, setQuery] = useState("");
  const layerFor: QuickFilterLayerFor = layerForProp ?? (() => filters);
  const trimmed = query.trim().toLowerCase();
  const isAvailable = useFilterTypeAvailability();

  const scopedWorkflowId = scope ? scopeWorkflowId(scope) : undefined;

  // Built off the config and the scope alone, so the index survives value
  // changes: the pools do not depend on what is selected in them.
  const defs = useMemo(
    () => scopeQuickFilterDefs(config.quickFilters ?? [], scope),
    [config.quickFilters, scope],
  );
  const roster = useAssigneeRoster(scope);
  const allSections = useQuickFilterOptions(
    defs,
    scopedWorkflowId,
    roster,
    (field) => layerFor(field).quickValues[field],
  );

  // A row over an empty pool is a dead end and hides — unless it holds an
  // applied value, which must stay inspectable and clearable.
  const sections = allSections.filter(
    ({ def }) =>
      isAvailable(def.type) ||
      hasQuickFilterValue(layerFor(def.field).quickValues[def.field]),
  );

  const kinds = useMemo(
    () => searchKinds(defs.map((def) => def.type)),
    [defs],
  );
  const server = useQuickSearchEntities(trimmed, kinds);

  const matches = useMemo(() => {
    if (!trimmed) return [];
    return sections.flatMap((section) => {
      const hits = searchOptions(section.options, section.def.type, trimmed, server);
      if (hits.length === 0) return [];
      return [
        {
          section,
          options: hits.slice(0, QUICK_SEARCH_MATCH_CAP),
          hidden: Math.max(0, hits.length - QUICK_SEARCH_MATCH_CAP),
        },
      ];
    });
  }, [sections, trimmed, server]);

  if (sections.length === 0) {
    return (
      <Box paddingX="sm" paddingY="sm">
        <Text size="sm" variant="muted">
          No quick filters configured for this view.
        </Text>
      </Box>
    );
  }

  return (
    <>
      {/* ONE search over the VALUES of every filter — the route in for a
          reader who knows the value but not which filter holds it. Its keys
          reach it because the popup releases the menu's typeahead to a text
          field (`releaseKeyboardToTextField`). Default size on the popup's
          own 8px edge — the CommandInput-inset grammar of every browsable
          popup. The dark fill drops to the page token so the field reads as
          a well IN the popover rather than a chip lifted off it. */}
      <Box paddingBottom="sm">
        <Input
          aria-label="Filter by anything"
          className="dark:bg-background"
          placeholder="Filter by anything…"
          ref={searchRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </Box>
      {trimmed ? (
        <SearchResults matches={matches} layerFor={layerFor} query={query} />
      ) : (
        groupSections(sections).map((run, index) => (
          <Box key={run.key} direction="column">
            {index > 0 ? <MenuSeparator /> : null}
            {/* Hairlines alone mark the runs: each holds a couple of rows
                that name themselves, so a heading over them repeats the rows
                and spends a line per run. The title stays for assistive
                tech, which cannot see the hairline. */}
            <MenuGroup aria-label={run.title}>
              {run.sections.map((section) => (
                <QuickFilterMenuRow
                  key={section.def.field}
                  section={section}
                  filters={layerFor(section.def.field)}
                />
              ))}
            </MenuGroup>
          </Box>
        ))
      )}
    </>
  );
}

/**
 * ONE filter's menu row.
 *
 * Two shapes, chosen by what the filter IS and never by how many values it has:
 * a set of values is a submenu; a lone flag is a checkbox row, because a
 * proposition to agree with has no panel to open beside it. The box is trailing
 * and drawn in both states: it lands in the gutter the other rows spend on
 * their chevron, and an empty box is what says the row is pressable where a
 * bare check would only appear once the flag is on.
 */
function QuickFilterMenuRow({
  section,
  filters,
}: {
  section: QuickFilterSection;
  filters: UseFiltersResult;
}) {
  const { def, options } = section;
  const value = filters.quickValues[def.field];
  const setValue = (next: unknown) => writeQuickValue(filters, section, next);

  if (def.type === "more-options") {
    const [flag] = options;
    if (!flag) return null;
    return (
      <MenuCheckboxItem
        variant="checkbox"
        checked={flag.selected(value)}
        onCheckedChange={() => setValue(flag.toggle(value))}
      >
        {/* 10px: the `lg` submenu trigger's gap, so this label starts on the
            same edge as the rows above it. */}
        <Box direction="row" align="center" gap={10}>
          {def.icon ? <Icon name={def.icon} size="sm" color="muted" /> : null}
          <Text size="sm">{def.label}</Text>
        </Box>
      </MenuCheckboxItem>
    );
  }

  return (
    <MenuSub>
      {/* `lg`: the 32px browsable-popup rung the checkbox rows already sit
          on, so a submenu row and a flag row read as one list. */}
      <MenuSubTrigger size="lg">
        {def.icon ? <Icon name={def.icon} size="sm" color="muted" /> : null}
        <Text size="sm">{def.label}</Text>
        {/* The readout TAKES the row's free space and sits at the end of it,
            rather than being pushed there: the chevron the trigger appends
            carries an auto margin of its own, and a second one here would split
            the gap between them and leave every value floating mid-row. With
            the space spent, the chevron's margin resolves to nothing and the
            values land on one right edge against it. */}
        <Box direction="row" align="center" justify="end" fill="flex" minWidth={0}>
          <QuickFilterValue section={section} value={value} />
        </Box>
      </MenuSubTrigger>
      {/* The first value reads as the row a press would act on, so the panel
          opens as a list with a place in it rather than a cold block. A look
          only: the pointer chose the ROW and has not reached a value in it. */}
      <MenuSubPopup
        autoHighlight={!isEntityPickerSection(section)}
        width="panel"
        padding={isEntityPickerSection(section) ? "none" : undefined}
      >
        {isEntityPickerSection(section) ? (
          <QuickEntityPicker section={section} value={value} onChange={setValue} />
        ) : def.type === "last-updated" ? (
          <QuickDateSubmenu
            options={options}
            value={value}
            onChange={setValue}
          />
        ) : (
          <QuickOptionSubmenu
            options={options}
            value={value}
            onChange={setValue}
            label={def.label}
          />
        )}
      </MenuSubPopup>
    </MenuSub>
  );
}

/**
 * What the cross-filter search found, grouped by the filter each value belongs
 * to. The rows toggle in place and the query survives the press, so several
 * values across several filters go on in one pass.
 */
function SearchResults({
  matches,
  layerFor,
  query,
}: {
  matches: {
    section: QuickFilterSection;
    options: QuickFilterOption[];
    hidden: number;
  }[];
  layerFor: QuickFilterLayerFor;
  query: string;
}) {
  if (matches.length === 0) {
    return (
      <Box paddingX="sm" paddingY="sm">
        <Text size="sm" variant="muted">
          {`No filter values match “${query.trim()}”.`}
        </Text>
      </Box>
    );
  }

  return (
    <>
      {matches.map(({ section, options, hidden }) => {
        const layer = layerFor(section.def.field);
        const value = layer.quickValues[section.def.field];
        return (
        <MenuGroup key={section.def.field}>
          <MenuGroupLabel>{section.def.label}</MenuGroupLabel>
          {options.map((option) => (
            <MenuCheckboxItem
              key={option.key}
              checked={option.selected(value)}
              onCheckedChange={() =>
                writeQuickValue(layer, section, option.toggle(value))
              }
            >
              <Box direction="row" align="center" gap="sm" minWidth={0}>
                <QuickOptionFace option={option} />
                {option.group ? (
                  <Text size="xs" variant="faint" truncate>
                    {option.group}
                  </Text>
                ) : null}
              </Box>
            </MenuCheckboxItem>
          ))}
          {hidden > 0 ? (
            <Box paddingX="sm" paddingY="2xs">
              <Text size="xs" variant="faint">
                {`${hidden} more — keep typing to narrow`}
              </Text>
            </Box>
          ) : null}
        </MenuGroup>
        );
      })}
    </>
  );
}
